import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { FormHelperText, Grid, IconButton, Tooltip } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SaveIcon from "@mui/icons-material/Save";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import { UserContext } from "App";

import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { uploadAlbumMedia, EditAlbumAPI, GetAlbumDetails, DeleteMediaAPI } from "APIs/AlbumAPIs";

import { popUp } from "components/Custom/AreYouSurePopUp";
import FullScreenLoader from "components/Custom/FullScreenLoader";

import { notify } from "App";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "edit-album-form";

const TITLE_ERROR = "Title is required";

// Smaller inner padding for the cards, so the content gets more width.
// (only affects the cards inside this wrapper, the shared SectionCard is not changed)
const compactCardsSx = {
  "& > div": {
    padding: { xs: "12px", md: "16px" },
  },
};

// Thumbnail frame. `isNew` = image selected but not uploaded yet (gold border).
const tileSx =
  (isNew = false) =>
  ({ palette, borders }) => ({
    position: "relative",
    display: "block",
    width: "100%",
    height: 140,
    overflow: "hidden",
    textDecoration: "none",
    borderRadius: borders.borderRadius.lg,
    backgroundColor: palette.light.main,
    border: isNew
      ? `2px solid ${palette.secondary.main}`
      : `${borders.borderWidth[1]} solid ${borders.borderColor}`,
  });

// Image with an "open in new tab" icon and a round "x" button in the corner
const ImageTile = ({ src, href, alt, isNew, onRemove }) => (
  <MDBox sx={tileSx(isNew)}>
    <img
      src={src}
      alt={alt}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
      }}
    />

    <MDBox
      sx={{
        position: "absolute",
        top: 6,
        right: 6,
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      <Tooltip title="Open in new tab">
        <IconButton
          size="small"
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            color: "#ffffff",
            padding: "4px",
          }}
        >
          <OpenInNewIcon style={{ fontSize: "14px" }} />
        </IconButton>
      </Tooltip>

      <MDBox
        component="button"
        type="button"
        onClick={onRemove}
        aria-label="Remove image"
        sx={({ palette }) => ({
          width: 26,
          height: 26,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 700,
          color: palette.white.main,
          backgroundColor: palette.error.main,
          transition: "background-color .3s ease",
          "&:hover": {
            backgroundColor: palette.primary.main,
          },
        })}
      >
        ✕
      </MDBox>
    </MDBox>
  </MDBox>
);

ImageTile.propTypes = {
  src: PropTypes.string.isRequired,
  href: PropTypes.string,
  alt: PropTypes.string,
  isNew: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
};

ImageTile.defaultProps = {
  href: "",
  alt: "album_img",
  isNew: false,
};

// Native file input (same accept list and behaviour as before), themed
const UploadInput = ({ onChange }) => (
  <MDBox
    component="input"
    type="file"
    name="image"
    multiple
    accept=".png,.jpg,.jpeg,.gif,.webp,.tiff,.psd,.bmp,.ico"
    onChange={onChange}
    sx={({ palette, borders }) => ({
      width: "100%",
      padding: "10px",
      cursor: "pointer",
      fontSize: 14,
      color: palette.text.main,
      border: `1px dashed ${palette.secondary.main}`,
      borderRadius: borders.borderRadius.lg,
      "&::file-selector-button": {
        marginRight: "12px",
        padding: "6px 14px",
        border: "none",
        borderRadius: borders.borderRadius.md,
        cursor: "pointer",
        color: palette.white.main,
        backgroundColor: palette.primary.main,
        transition: "background-color .3s ease",
      },
      "&::file-selector-button:hover": {
        backgroundColor: palette.secondary.main,
      },
    })}
  />
);

UploadInput.propTypes = {
  onChange: PropTypes.func.isRequired,
};

const EditAlbum = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const { accentName } = useAccent();

  const [album, setAlbum] = useState({
    media_album_id: "",
    media_album_title: "",
  });

  const [existingMedia, setExistingMedia] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({});
  const formRef = useRef(null);
  const [scrollToErrorKey, setScrollToErrorKey] = useState(0);

  const [isFetchingData, setIsFetchingData] = useState(true);

  // After the errors have been rendered, scroll to the first one
  useEffect(() => {
    if (!scrollToErrorKey) return;
    formRef.current
      ?.querySelector(".Mui-error")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [scrollToErrorKey]);

  const handleBlur = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

  // The error only appears after the field is touched (blurred, or Save was clicked)
  const isTitleEmpty = String(album.media_album_title || "").trim() === "";
  const titleError = touched.media_album_title && isTitleEmpty ? TITLE_ERROR : "";
  const isFormValid = !isTitleEmpty;

  // Fetch album details
  const getAlbumData = async (albumId, access_token, dispatchFunc) => {
    setIsFetchingData(true);
    const response = await GetAlbumDetails(albumId, access_token, dispatchFunc);
    if (response !== null) {
      setAlbum({
        media_album_id: response.media_album_id || "",
        media_album_title: response.media_album_title || "",
      });

      setExistingMedia(response.list || []);
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData?.access_token !== undefined) {
      getAlbumData(id, userData.access_token, dispatch);
    }
  }, [userData, id]);

  const handleAlbumDataChange = (e) => {
    setAlbum((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Handle selecting new files and local object previews
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);

      const newImageObjects = selectedFiles.map((file) => ({
        id: Math.random().toString(36).substring(2, 9),
        file: file,
        preview: URL.createObjectURL(file),
      }));

      setNewFiles((prev) => [...prev, ...newImageObjects]);

      e.target.value = "";
    }
  };

  // Delete existing media from backend list
  const handleDeleteExistingMedia = async (mediaId) => {
    await popUp(async () => {
      // =========================================================
      // TODO: Call API to delete album media item when available:
      // await DeleteAlbumMediaAPI(mediaId, userData.access_token, dispatch);
      // =========================================================

      await DeleteMediaAPI(mediaId, userData.access_token);

      setExistingMedia((prev) => prev.filter((item) => item.media_id !== mediaId));
    });
  };

  // Delete newly picked local image preview
  const handleDeleteNewImage = (fileId) => {
    setNewFiles((prev) => {
      const fileToRemove = prev.find((item) => item.id === fileId);

      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      return prev.filter((item) => item.id !== fileId);
    });
  };

  // Submit Handler
  const handleSubmit = async () => {
    setLoading(true);

    try {
      const AlbumResponse = await EditAlbumAPI(album, userData.access_token, dispatch);

      if (AlbumResponse !== null) {
        if (newFiles.length > 0) {
          let formData = new FormData();

          formData.append("media_album_id", album.media_album_id);

          newFiles.forEach((item) => {
            formData.append("files", item.file);
          });

          await uploadAlbumMedia(formData, userData.access_token, dispatch);
        }

        notify("Album Edited!", "success");

        navigate("/album");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      <SectionHeader
        icon="edit"
        label="EDIT ALBUM"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </MDButton>
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          <form
            ref={formRef}
            id={FORM_ID}
            noValidate
            autoComplete="off"
            onSubmit={async (e) => {
              e.preventDefault();

              if (loading) return;

              // Invalid form: show the error and scroll to it
              if (!isFormValid) {
                setTouched({ media_album_title: true });
                setScrollToErrorKey((key) => key + 1);
                return;
              }

              // Custom message for the save confirmation (delete popups keep the default text)
              await popUp(handleSubmit, undefined, {
                title: "Save changes?",
                text: "Your changes to this album will be saved.",
                icon: "question",
                confirmButtonText: "Yes, save",
                cancelButtonText: "Cancel",
              });
            }}
          >
            <MDBox sx={compactCardsSx}>
              <SectionCard title="Details" subtitle="Fields marked * are required">
                <Grid container spacing={2}>
                  <FormField name="title" required full>
                    <MDInput
                      name="media_album_title"
                      type="text"
                      placeholder="Enter title"
                      value={album?.media_album_title || ""}
                      fullWidth
                      onChange={handleAlbumDataChange}
                      onBlur={() => handleBlur("media_album_title")}
                      error={!!titleError}
                    />
                    {titleError && <FormHelperText error>{titleError}</FormHelperText>}
                  </FormField>
                </Grid>
              </SectionCard>

              <SectionCard title="Media" subtitle="Remove existing images or add new ones">
                <Grid container spacing={2}>
                  {/* Existing Media Gallery */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color={accentName}
                      display="block"
                      mb={1.5}
                    >
                      Existing Media ({existingMedia.length})
                    </MDTypography>

                    {existingMedia.length > 0 ? (
                      <Grid container spacing={2}>
                        {existingMedia.map((item) => (
                          <Grid item key={item.media_id} xs={6} sm={4} md={3}>
                            <ImageTile
                              src={item.media_location}
                              href={item.media_location}
                              alt={`media_${item.media_id}`}
                              onRemove={() => handleDeleteExistingMedia(item.media_id)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <MDTypography variant="button" color="text">
                        No existing media attached to this album.
                      </MDTypography>
                    )}
                  </Grid>

                  {/* New Files Preview Grid */}
                  {newFiles.length > 0 && (
                    <Grid item xs={12}>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color={accentName}
                        display="block"
                        mb={1.5}
                      >
                        New Files Selected ({newFiles.length})
                      </MDTypography>

                      <Grid container spacing={2}>
                        {newFiles.map((item) => (
                          <Grid item key={item.id} xs={6} sm={4} md={3}>
                            <ImageTile
                              isNew
                              src={item.preview}
                              href={item.preview}
                              alt="new_preview"
                              onRemove={() => handleDeleteNewImage(item.id)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  {/* Upload New Media */}
                  <FormField name="upload_additional_media" full>
                    <UploadInput onChange={handleFileChange} />
                  </FormField>
                </Grid>
              </SectionCard>
            </MDBox>
          </form>

          {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save still submits the form through the `form` attribute. */}
          <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
            <MDButton
              type="button"
              variant="outlined"
              color="primary"
              sx={ranuOutlinedButtonSx}
              disabled={loading}
              onClick={() => navigate("/album")}
            >
              Cancel
            </MDButton>
            <MDButton
              color="primary"
              type="submit"
              form={FORM_ID}
              sx={ranuButtonSx}
              disabled={loading}
            >
              Save Album
            </MDButton>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default EditAlbum;
